import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  buscarPersonas, buscarPorDocumento, crearPersona,
  crearSuscripcion, actualizarSuscripcion, actualizarEstadoSuscripcion,
  actualizarPersona,
  getKits, getPorterosActivos, getBarrios,
} from '../../lib/clientesApi'
import {
  getPagosCliente, registrarPago, crearPagoManual, cancelarPago,
  getCuentaCobro, textoWhatsAppCobro,
  ESTADO_PAGO_LABEL, ESTADO_PAGO_COLOR,
} from '../../lib/cobrosApi'
import { getSuscripciones } from '../../lib/entregasApi'
import { validarDocumento, validarEmail, validarTelUY, formatearTelUY } from '../../lib/documentos'
import { geocodificar } from '../../lib/geo'
import MapPin from '../../components/MapPin'

const VIVIENDA_LABEL = { casa: '🏠 Casa', edificio: '🏢 Edificio', oficina: '🏢 Oficina' }
const ESTADO_COLOR = {
  activa: { bg: '#D1FAE5', text: '#065F46' },
  pausada: { bg: '#FEF3C7', text: '#92400E' },
  cancelada: { bg: '#FEE2E2', text: '#991B1B' },
}

export default function Clientes() {
  const [suscripciones, setSuscripciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todas')  // todas | activa | pausada | cancelada
  const [busqueda, setBusqueda] = useState('')
  const [showAlta, setShowAlta] = useState(false)
  const [editando, setEditando] = useState(null)  // { ...suscripcion } o null

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setSuscripciones(await getSuscripciones())
    setLoading(false)
  }

  const filtradas = useMemo(() => {
    let r = suscripciones
    if (filtro !== 'todas') r = r.filter(s => s.estado === filtro)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      r = r.filter(s =>
        s.titular?.nombre?.toLowerCase().includes(q) ||
        s.beneficiario?.nombre?.toLowerCase().includes(q) ||
        s.barrio_obj?.nombre?.toLowerCase().includes(q) ||
        s.barrio?.toLowerCase().includes(q) ||
        s.direccion?.toLowerCase().includes(q) ||
        s.alias?.toLowerCase().includes(q)
      )
    }
    return r
  }, [suscripciones, filtro, busqueda])

  const counts = useMemo(() => ({
    todas: suscripciones.length,
    activa: suscripciones.filter(s => s.estado === 'activa').length,
    pausada: suscripciones.filter(s => s.estado === 'pausada').length,
    cancelada: suscripciones.filter(s => s.estado === 'cancelada').length,
  }), [suscripciones])

  return (
    <AdminLayout>
      <div className="page">
        <h1>Clientes</h1>
        <p className="lead">Suscripciones · titulares y beneficiarios · una persona puede tener varias</p>
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Total suscripciones</h3>
            <div className="stat">{counts.todas}</div>
          </div>
          <div className="card">
            <h3>Activas</h3>
            <div className="stat" style={{ color: '#10B981' }}>{counts.activa}</div>
          </div>
          <div className="card">
            <h3>Pausadas</h3>
            <div className="stat" style={{ color: '#F59E0B' }}>{counts.pausada}</div>
          </div>
          <div className="card">
            <h3>Canceladas</h3>
            <div className="stat" style={{ color: '#EF4444' }}>{counts.cancelada}</div>
          </div>
        </div>

        <div className="btn-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={() => setShowAlta(true)}>+ Nueva suscripción</button>
          <Link to="/admin/clientes/fuera-zona" className="btn ghost" style={{ borderColor: '#FCA5A5', color: '#B91C1C' }} title="Alta excepción para clientes fuera de cobertura">🚫 + Fuera de zona</Link>
          <input
            type="search"
            placeholder="Buscar por nombre, dirección, barrio…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--line)', fontSize: 14 }}
          />
          <button className="btn ghost" onClick={load}>↺</button>
        </div>

        <div className="btn-row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
          {['todas', 'activa', 'pausada', 'cancelada'].map(f => (
            <button
              key={f}
              className={`btn ghost ${filtro === f ? 'active' : ''}`}
              style={{ padding: '4px 10px', fontSize: 12, ...(filtro === f ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : {}) }}
              onClick={() => setFiltro(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--muted)' }}>
            {suscripciones.length === 0
              ? 'No hay suscripciones todavía. Tocá "+ Nueva suscripción" para empezar.'
              : 'No hay suscripciones con esos filtros.'}
          </div>
        ) : (
          <table className="t-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Recibe</th>
                <th>Dirección</th>
                <th>Kit · Día</th>
                <th>Origen</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(s => {
                const recibe = s.beneficiario || s.titular
                const esRegalo = s.beneficiario && s.beneficiario.id !== s.titular?.id
                const cfg = ESTADO_COLOR[s.estado] || ESTADO_COLOR.activa
                return (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{recibe?.nombre || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{recibe?.phone || '—'}</div>
                      {esRegalo && (
                        <div style={{ fontSize: 10, color: '#7C3AED', marginTop: 2, fontWeight: 700 }}>
                          🎁 paga: {s.titular?.nombre}
                        </div>
                      )}
                    </td>
                    <td>
                      {s.alias && <div style={{ fontSize: 11, fontWeight: 800, color: '#0F1F3D', textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.alias}</div>}
                      {s.fuera_zona && (
                        <div style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 8px', background: '#FEF3C7', color: '#92400E', borderRadius: 999, marginBottom: 4 }}>
                          🚫 Fuera de zona{s.entrega_responsable ? ` · ${s.entrega_responsable}` : ''}
                        </div>
                      )}
                      <div>{s.barrio_obj?.nombre || s.barrio || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {s.direccion}{s.apto ? ` · ${s.apto}` : ''}
                      </div>
                      {s.tipo_vivienda && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{VIVIENDA_LABEL[s.tipo_vivienda]}</div>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{s.kit?.nombre || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'capitalize' }}>
                        {s.semana_mes && <strong style={{ color: '#0F1F3D' }}>{s.semana_mes}ra sem. </strong>}
                        {s.dia_entrega || '—'} · {s.ventana_horaria === 'tarde' ? '14-18hs' : '10-14hs'}
                      </div>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {s.portero?.lugar
                        ? <><div style={{ fontWeight: 600 }}>🏛 {s.portero.lugar}</div><div style={{ color: 'var(--muted)' }}>{s.portero.profile?.nombre}</div></>
                        : <span style={{ color: 'var(--muted)' }}>directo</span>}
                    </td>
                    <td>
                      <span style={{ background: cfg.bg, color: cfg.text, padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        ● {s.estado}
                      </span>
                      {s.pausa_hasta && (
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>hasta {s.pausa_hasta}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn ghost"
                          style={{ ...btnSm, fontWeight: 700 }}
                          onClick={() => setEditando(s)}
                          title="Ver / editar"
                        >✏️</button>
                        <BotonesAccion suscripcion={s} onChange={load} />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {showAlta && (
          <ModalAlta
            onClose={() => setShowAlta(false)}
            onCreated={() => { setShowAlta(false); load() }}
          />
        )}

        {editando && (
          <ModalEditar
            suscripcion={editando}
            onClose={() => setEditando(null)}
            onSaved={() => { setEditando(null); load() }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

function BotonesAccion({ suscripcion: s, onChange }) {
  async function pausar() {
    const fecha = prompt('¿Hasta qué fecha? (YYYY-MM-DD, vacío = sin límite)')
    if (fecha === null) return
    try {
      await actualizarEstadoSuscripcion(s.id, 'pausada', fecha || null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  async function reactivar() {
    try {
      await actualizarEstadoSuscripcion(s.id, 'activa', null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  async function cancelar() {
    if (!confirm('¿Cancelar esta suscripción? Se puede reactivar después.')) return
    try {
      await actualizarEstadoSuscripcion(s.id, 'cancelada', null)
      onChange()
    } catch (e) { alert(e.message) }
  }
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {s.estado === 'activa' && <button className="btn ghost" style={btnSm} onClick={pausar}>⏸</button>}
      {s.estado === 'pausada' && <button className="btn ghost" style={btnSm} onClick={reactivar}>▶</button>}
      {s.estado !== 'cancelada' && <button className="btn ghost" style={{ ...btnSm, color: '#991B1B' }} onClick={cancelar}>✕</button>}
    </div>
  )
}
const btnSm = { padding: '4px 8px', fontSize: 11 }

/* ───────── Modal Alta (single page con checkbox beneficiario) ───────── */

function ModalAlta({ onClose, onCreated }) {
  const [titular, setTitular] = useState(null)
  const [beneficiario, setBeneficiario] = useState(null)
  const [mismoBenef, setMismoBenef] = useState(true)  // checkbox: el beneficiario es el mismo
  const [form, setForm] = useState({
    alias: '', barrioId: '', direccion: '', apto: '', tipoVivienda: '',
    kitCodigo: '', diaEntrega: '', ventanaHoraria: 'manana', semanaMes: '',
    diaCobroMes: String(new Date().getDate()),  // default: día de hoy
    modeloCobro: 'prepaid_mensiversario',
    porteroReferidoId: '', notas: '',
  })
  const [coords, setCoords] = useState(null)  // { lat, lng } para el pin del mapa
  const [coordsExactas, setCoordsExactas] = useState(false)  // checkbox "el pin está bien"
  const [geoErr, setGeoErr] = useState(null)
  const [geocodando, setGeocodando] = useState(false)
  const [barrios, setBarrios] = useState([])
  const [kits, setKits] = useState([])
  const [porteros, setPorteros] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Barrio seleccionado (objeto completo) y días disponibles
  const barrioSeleccionado = useMemo(() => barrios.find(b => b.id === form.barrioId), [barrios, form.barrioId])
  const diasDisponibles = barrioSeleccionado?.dias || []

  /** Busca la dirección en OSM y pone el pin. Si falla, cae al centro del barrio. */
  async function geocodearDireccion() {
    setGeoErr(null)
    setCoordsExactas(false)
    const barrioNombre = barrioSeleccionado?.nombre
    if (!barrioNombre) return
    setGeocodando(true)
    try {
      // 1. Intentar dirección exacta
      if (form.direccion.trim()) {
        const r = await geocodificar(`${form.direccion}, ${barrioNombre}`)
        if (r) {
          setCoords({ lat: r.lat, lng: r.lng })
          setCoordsExactas(true)  // Si OSM la encontró, asumimos que está bien
          return
        }
      }
      // 2. Fallback: centro del barrio (siempre tenemos algo donde mover el pin)
      const rb = await geocodificar(barrioNombre)
      if (rb) {
        setCoords({ lat: rb.lat, lng: rb.lng })
        setGeoErr('No pude encontrar la dirección exacta. El pin está en el centro del barrio — movelo al lugar correcto.')
      } else {
        // Última opción: centro de Montevideo
        setCoords({ lat: -34.9011, lng: -56.1645 })
        setGeoErr('Centro de Montevideo. Movelo al lugar correcto.')
      }
    } finally { setGeocodando(false) }
  }

  useEffect(() => {
    Promise.all([getKits(), getPorterosActivos(), getBarrios()]).then(([k, p, b]) => {
      setKits(k); setPorteros(p); setBarrios(b)
    })
  }, [])

  async function handleSubmit() {
    setErr(null); setSaving(true)
    try {
      if (!titular) throw new Error('Falta cargar quien paga (titular)')
      if (!mismoBenef && !beneficiario) throw new Error('Destildaste "el mismo" pero no cargaste al beneficiario')
      if (!form.barrioId || !form.direccion.trim()) throw new Error('Falta elegir barrio y dirección')
      if (form.diaEntrega && !diasDisponibles.includes(form.diaEntrega)) {
        throw new Error('El día elegido no está disponible para ese barrio')
      }
      const r = await crearSuscripcion({
        titularId: titular.id,
        beneficiarioId: mismoBenef ? null : beneficiario?.id,
        alias: form.alias,
        barrioId: form.barrioId,
        barrioNombre: barrioSeleccionado.nombre,
        direccion: form.direccion,
        apto: form.apto || null,
        tipoVivienda: form.tipoVivienda || null,
        kitCodigo: form.kitCodigo || null,
        diaEntrega: form.diaEntrega || null,
        ventanaHoraria: form.ventanaHoraria,
        semanaMes: form.semanaMes || null,
        diaCobroMes: form.diaCobroMes ? Number(form.diaCobroMes) : null,
        modeloCobro: form.modeloCobro,
        porteroReferidoId: form.porteroReferidoId || null,
        notas: form.notas,
        coordsManual: coords,  // las coords del pin (override del geocoding automático)
      })
      onCreated(r)
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <h2 style={{ margin: '0 0 6px' }}>Nueva suscripción</h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0, marginBottom: 18 }}>
          Cargá quién paga, quién recibe (si es la misma marcá la casilla) y los datos de entrega.
        </p>

        {/* TITULAR */}
        <h3 className="section-head" style={{ marginTop: 0 }}>💳 Quien paga la suscripción</h3>
        <BuscarPersona seleccionada={titular} onSelect={setTitular} esTitular={true} />

        {titular && (
          <>
            {/* CHECKBOX MISMO */}
            <div style={{ marginTop: 18, padding: '12px 14px', background: mismoBenef ? '#ECFDF5' : '#FEF3C7', border: `2px solid ${mismoBenef ? '#10B981' : '#F59E0B'}`, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                <input
                  type="checkbox"
                  checked={mismoBenef}
                  onChange={e => { setMismoBenef(e.target.checked); if (e.target.checked) setBeneficiario(null) }}
                  style={{ width: 18, height: 18 }}
                />
                <span>📦 El beneficiario es el mismo que el titular</span>
              </label>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, paddingLeft: 28 }}>
                {mismoBenef
                  ? <>Va a recibir <strong>{titular.nombre}</strong> en la dirección que cargues abajo.</>
                  : <>Tildá la casilla si quien recibe es la misma persona que paga, o cargá abajo a quién recibe (regalo, familiar, etc.)</>}
              </div>
            </div>

            {/* BENEFICIARIO (solo si destildó) */}
            {!mismoBenef && (
              <div style={{ marginTop: 14 }}>
                <h3 className="section-head" style={{ marginTop: 0 }}>🎁 Quien recibe el pedido</h3>
                <BuscarPersona seleccionada={beneficiario} onSelect={setBeneficiario} esTitular={false} />
              </div>
            )}

            {/* DATOS SUSCRIPCIÓN */}
            <h3 className="section-head" style={{ marginTop: 22 }}>📦 Datos de la suscripción</h3>
            <Field label="Alias (para distinguir si tiene varias)">
              <input type="text" value={form.alias} onChange={e => setForm({...form, alias: e.target.value})} placeholder="Ej: Casa, Mamá, Oficina" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Barrio" required>
                <select
                  required
                  value={form.barrioId}
                  onChange={e => setForm({...form, barrioId: e.target.value, diaEntrega: ''})}
                >
                  <option value="">— elegir barrio —</option>
                  {barrios.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
              </Field>
              <Field label="Tipo">
                <select value={form.tipoVivienda} onChange={e => setForm({...form, tipoVivienda: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="casa">🏠 Casa</option>
                  <option value="edificio">🏢 Edificio</option>
                  <option value="oficina">🏢 Oficina</option>
                </select>
              </Field>
            </div>
            <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '6px 10px', fontSize: 11, marginTop: -6, marginBottom: 10, color: '#92400E' }}>
              ¿No está el barrio del cliente? Lamentablemente <strong>no estamos en esa zona todavía</strong>. Decile que próximamente evaluaremos cubrirla.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <Field label="Calle + número" required>
                <input
                  type="text" required
                  value={form.direccion}
                  onChange={e => setForm({...form, direccion: e.target.value})}
                  onBlur={geocodearDireccion}
                  placeholder="Cavia 2858"
                />
              </Field>
              <Field label="Apto / Piso / Casa">
                <input
                  type="text"
                  value={form.apto}
                  onChange={e => setForm({...form, apto: e.target.value})}
                  placeholder="Apto 406"
                />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: -6, marginBottom: 12 }}>
              <button
                type="button"
                onClick={geocodearDireccion}
                disabled={!form.barrioId || geocodando}
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: '#0F1F3D', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}
              >
                {geocodando ? '⏳ Buscando…' : coords ? '🗺️ Recargar mapa' : '🗺️ Abrir mapa'}
              </button>
              {coords && coordsExactas && <span style={{ fontSize: 11, color: '#065F46', fontWeight: 700 }}>✓ pin confirmado</span>}
              {coords && !coordsExactas && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 700 }}>⚠️ verificá el pin abajo</span>}
              {geoErr && <span style={{ fontSize: 11, color: '#92400E' }}>{geoErr}</span>}
            </div>

            {coords && (
              <>
                <MapPin
                  lat={coords.lat}
                  lng={coords.lng}
                  direccion={`${form.direccion}, ${barrioSeleccionado?.nombre || ''}`}
                  onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }}
                  height={260}
                />
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  marginTop: 8, padding: '10px 14px', borderRadius: 8,
                  border: coordsExactas ? '2px solid #10B981' : '2px solid #F59E0B',
                  background: coordsExactas ? '#ECFDF5' : '#FEF3C7',
                  fontSize: 13, fontWeight: 600,
                }}>
                  <input
                    type="checkbox"
                    checked={coordsExactas}
                    onChange={e => setCoordsExactas(e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>{coordsExactas ? '✅ El pin está en la ubicación correcta' : '⚠️ Confirmá que el pin está en el lugar correcto'}</span>
                </label>
              </>
            )}

            <Field label="Kit">
              <select value={form.kitCodigo} onChange={e => setForm({...form, kitCodigo: e.target.value})}>
                <option value="">— sin kit —</option>
                {kits.map(k => <option key={k.codigo} value={k.codigo}>{k.nombre}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                💡 A futuro: pantalla visual de comparación de kits (con fotos + contenido + precio).
              </div>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Día de entrega" required={!!form.barrioId}>
                <select
                  value={form.diaEntrega}
                  onChange={e => setForm({...form, diaEntrega: e.target.value})}
                  disabled={!form.barrioId}
                >
                  <option value="">{form.barrioId ? '— elegir día —' : '— elegí barrio primero —'}</option>
                  {diasDisponibles.map(d =>
                    <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>
                  )}
                </select>
                {form.barrioId && diasDisponibles.length === 0 && (
                  <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>Este barrio no tiene días configurados</div>
                )}
              </Field>
              <Field label="Semana del mes" required>
                <select value={form.semanaMes} onChange={e => setForm({...form, semanaMes: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="1">1ra (días 1-7)</option>
                  <option value="2">2da (días 8-14)</option>
                  <option value="3">3ra (días 15-21)</option>
                  <option value="4">4ta (días 22-31)</option>
                </select>
              </Field>
              <Field label="Ventana">
                <select value={form.ventanaHoraria} onChange={e => setForm({...form, ventanaHoraria: e.target.value})}>
                  <option value="manana">Mañana 10-14hs</option>
                  <option value="tarde">Tarde 14-18hs</option>
                </select>
              </Field>
            </div>

            <Field label="Portero referido (si vino por uno)">
              <select value={form.porteroReferidoId} onChange={e => setForm({...form, porteroReferidoId: e.target.value})}>
                <option value="">— directo, sin referido —</option>
                {porteros.map(p => <option key={p.id} value={p.id}>{p.lugar} · {p.profile?.nombre}</option>)}
              </select>
            </Field>

            <Field label="Modelo de cobro" required>
              <select value={form.modeloCobro} onChange={e => setForm({...form, modeloCobro: e.target.value})}>
                <option value="prepaid_mensiversario">🔄 Mensiversario (cliente normal — paga siempre el mismo día del mes)</option>
                <option value="postpaid_entrega">🤝 Al entregar (amigos/familia — pagás cuando recibís)</option>
                <option value="gratis">🎁 Gratis (tester / regalo — no se cobra)</option>
              </select>
            </Field>

            {form.modeloCobro === 'prepaid_mensiversario' && (
              <Field label="Día del mes en que se cobra (1-31)" required>
                <input
                  type="number" min="1" max="31"
                  value={form.diaCobroMes}
                  onChange={e => setForm({...form, diaCobroMes: e.target.value})}
                  placeholder="15"
                />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  💡 Default: hoy. Es el día del mes en que se le cobra todos los meses.
                </div>
              </Field>
            )}

            <Field label="Notas (instrucciones de entrega, etc.)">
              <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" placeholder="Llamar al timbre 8B..." />
            </Field>
          </>
        )}

        {err && <div className="err-box" style={{ marginTop: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 18, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSubmit} disabled={saving || !titular}>
            {saving ? 'Guardando…' : '✓ Crear suscripción'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* Buscar persona o crear nueva.
   Si esTitular = true → más datos obligatorios (CI/RUT/email).
   Si esTitular = false → solo nombre + teléfono obligatorios (es solo destinatario). */
function BuscarPersona({ seleccionada, onSelect, esTitular = true }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [showCrear, setShowCrear] = useState(false)

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return }
    setSearching(true)
    const t = setTimeout(async () => {
      const r = await buscarPersonas(q)
      setResults(r); setSearching(false)
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  if (seleccionada) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, border: '2px solid #10B981', background: '#ECFDF5', borderRadius: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700 }}>✓ {seleccionada.nombre}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {seleccionada.documento && <span>{seleccionada.documento_tipo?.toUpperCase()} {seleccionada.documento}</span>}
            {seleccionada.phone && <span>📞 {seleccionada.phone}</span>}
            {seleccionada.email && <span>✉️ {seleccionada.email}</span>}
          </div>
        </div>
        <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onSelect(null)}>Cambiar</button>
      </div>
    )
  }

  if (showCrear) {
    return <FormCrearPersona
      esTitular={esTitular}
      onCancel={() => setShowCrear(false)}
      onCreated={p => { onSelect(p); setShowCrear(false) }}
    />
  }

  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Buscar por nombre, teléfono, email o documento…"
        autoFocus
        style={{ width: '100%', padding: 10, fontSize: 14, borderRadius: 8, border: '1px solid var(--line)' }}
      />
      {searching && <div style={{ padding: 8, fontSize: 12, color: 'var(--muted)' }}>Buscando…</div>}
      {results.length > 0 && (
        <div style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          {results.map(r => (
            <div
              key={r.id}
              onClick={() => onSelect(r)}
              style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid var(--line)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--paper)'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 700 }}>{r.nombre}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {r.documento && <span>{r.documento_tipo?.toUpperCase()} {r.documento}</span>}
                {r.phone && <span>📞 {r.phone}</span>}
                {r.email && <span>✉️ {r.email}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => setShowCrear(true)}
        style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, border: '1.5px dashed var(--navy)', background: '#fff', color: 'var(--navy)', fontWeight: 700, fontSize: 13, cursor: 'pointer', width: '100%' }}
      >+ Crear persona nueva</button>
    </div>
  )
}

/* Sub-form para crear persona con validación de documento + detector de duplicados */
function FormCrearPersona({ esTitular, onCancel, onCreated }) {
  const [f, setF] = useState({
    nombre: '', phone: '', email: '',
    documentoTipo: 'ci', documento: '',
  })
  const [docErr, setDocErr] = useState(null)
  const [duplicado, setDuplicado] = useState(null)
  const [emailErr, setEmailErr] = useState(null)
  const [telErr, setTelErr] = useState(null)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)

  // Detección de duplicado por documento (con debounce)
  useEffect(() => {
    setDuplicado(null); setDocErr(null)
    if (!f.documento.trim()) return
    const v = validarDocumento(f.documentoTipo, f.documento)
    if (!v.ok) { setDocErr(v.msg); return }
    const t = setTimeout(async () => {
      const dup = await buscarPorDocumento(f.documentoTipo, v.normalizado)
      if (dup) setDuplicado(dup)
    }, 400)
    return () => clearTimeout(t)
  }, [f.documento, f.documentoTipo])

  // Validar email on blur
  function checkEmail() {
    if (!f.email.trim()) { setEmailErr(null); return }
    setEmailErr(validarEmail(f.email) ? null : 'Email inválido')
  }
  function checkTel() {
    if (!f.phone.trim()) { setTelErr(null); return }
    setTelErr(validarTelUY(f.phone) ? null : 'Teléfono uruguayo inválido (ej: 098 123 456)')
  }

  async function handleCrear() {
    setErr(null)
    if (!f.nombre.trim()) { setErr('Falta el nombre'); return }
    if (esTitular) {
      if (!f.documento.trim()) { setErr('Para el titular es obligatorio el documento'); return }
      // docErr es solo warning, NO bloquea
      if (!f.email.trim()) { setErr('Para el titular es obligatorio el email (para facturas)'); return }
      if (!validarEmail(f.email)) { setErr('Email inválido'); return }
    }
    if (!f.phone.trim()) { setErr('El teléfono es obligatorio (para WhatsApp)'); return }
    if (!validarTelUY(f.phone)) { setErr('Teléfono uruguayo inválido'); return }
    // Warning del documento NO bloquea el guardado

    setCreando(true)
    try {
      const v = validarDocumento(f.documentoTipo, f.documento)
      const p = await crearPersona({
        nombre: f.nombre,
        phone: formatearTelUY(f.phone),
        email: f.email,
        documento: v.normalizado,
        documentoTipo: f.documento.trim() ? f.documentoTipo : null,
      })
      onCreated(p)
    } catch (e) {
      const msg = String(e.message || '')
      if (msg.includes('profiles_documento_unq')) setErr('Ya existe alguien con ese documento. Buscá arriba en la lista.')
      else if (msg.includes('profiles_email_unq')) setErr('Ya existe alguien con ese email.')
      else setErr(msg)
    }
    finally { setCreando(false) }
  }

  return (
    <div style={{ background: 'var(--paper)', padding: 14, borderRadius: 8 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>Crear persona nueva</h4>

      <Field label="Nombre completo" required>
        <input type="text" value={f.nombre} onChange={e => setF({...f, nombre: e.target.value})} placeholder="María Fernández" autoFocus />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8 }}>
        <Field label="Tipo doc">
          <select value={f.documentoTipo} onChange={e => setF({...f, documentoTipo: e.target.value, documento: ''})}>
            <option value="ci">CI</option>
            <option value="rut">RUT</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </Field>
        <Field label={esTitular ? 'Documento' : 'Documento (opcional)'} required={esTitular}>
          <input
            type="text"
            value={f.documento}
            onChange={e => setF({...f, documento: e.target.value})}
            placeholder={f.documentoTipo === 'ci' ? '4.123.456-7' : f.documentoTipo === 'rut' ? '212345670012' : 'AB123456'}
            style={{ borderColor: docErr ? '#F59E0B' : undefined }}
          />
          {docErr && (
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 3, background: '#FEF3C7', padding: '4px 6px', borderRadius: 4 }}>
              ⚠️ {docErr}. Si estás seguro de que es correcto, podés guardar igual.
            </div>
          )}
        </Field>
      </div>

      {duplicado && (
        <div style={{ background: '#FEF3C7', border: '1.5px solid #F59E0B', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
          ⚠️ Ya existe <strong>{duplicado.nombre}</strong> con ese documento.
          <button
            onClick={() => onCreated(duplicado)}
            style={{ marginLeft: 8, padding: '4px 10px', background: '#F59E0B', color: '#fff', border: 0, borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}
          >Usar esta persona →</button>
        </div>
      )}

      <Field label={esTitular ? 'Email (para facturas)' : 'Email (opcional)'} required={esTitular}>
        <input
          type="email"
          value={f.email}
          onChange={e => setF({...f, email: e.target.value})}
          onBlur={checkEmail}
          placeholder="maria@ejemplo.com"
          style={{ borderColor: emailErr ? '#EF4444' : undefined }}
        />
        {emailErr && <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>{emailErr}</div>}
      </Field>

      <Field label="Teléfono móvil" required>
        <input
          type="tel"
          value={f.phone}
          onChange={e => setF({...f, phone: e.target.value})}
          onBlur={checkTel}
          placeholder="098 123 456"
          style={{ borderColor: telErr ? '#EF4444' : undefined }}
        />
        {telErr && <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>{telErr}</div>}
      </Field>

      {err && <div className="err-box" style={{ marginTop: 6 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="btn ghost" onClick={onCancel}>Cancelar</button>
        <button className="btn primary" onClick={handleCrear} disabled={creando || !f.nombre.trim()}>
          {creando ? 'Creando…' : '+ Crear'}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}

/* ───────── Modal de edición de suscripción + persona ───────── */

function ModalEditar({ suscripcion: s, onClose, onSaved }) {
  const [tab, setTab] = useState('suscripcion')  // suscripcion | titular | beneficiario
  const [barrios, setBarrios] = useState([])
  const [kits, setKits] = useState([])
  const [porteros, setPorteros] = useState([])

  // Estado del form de suscripción
  const [form, setForm] = useState({
    alias: s.alias || '',
    barrioId: s.barrio_obj?.id || '',
    direccion: s.direccion || '',
    apto: s.apto || '',
    tipoVivienda: s.tipo_vivienda || '',
    kitCodigo: s.kit_actual || '',
    diaEntrega: s.dia_entrega || '',
    ventanaHoraria: s.ventana_horaria || 'manana',
    semanaMes: s.semana_mes || '',
    diaCobroMes: s.dia_cobro_mes || '',
    modeloCobro: s.modelo_cobro || 'prepaid_mensiversario',
    porteroReferidoId: s.portero?.id || '',
    notas: s.notas || '',
  })
  const [coords, setCoords] = useState(s.lat ? { lat: Number(s.lat), lng: Number(s.lng) } : null)
  const [coordsExactas, setCoordsExactas] = useState(true)  // si tiene coords ya, asumimos OK
  const [geocodando, setGeocodando] = useState(false)
  const [geoErr, setGeoErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const barrioSeleccionado = useMemo(() => barrios.find(b => b.id === form.barrioId), [barrios, form.barrioId])
  const diasDisponibles = barrioSeleccionado?.dias || []

  useEffect(() => {
    Promise.all([getKits(), getPorterosActivos(), getBarrios()]).then(([k, p, b]) => {
      setKits(k); setPorteros(p); setBarrios(b)
    })
  }, [])

  async function geocodear() {
    setGeoErr(null)
    setCoordsExactas(false)
    const barrioNombre = barrioSeleccionado?.nombre
    if (!barrioNombre) return
    setGeocodando(true)
    try {
      if (form.direccion.trim()) {
        const r = await geocodificar(`${form.direccion}, ${barrioNombre}`)
        if (r) { setCoords({ lat: r.lat, lng: r.lng }); setCoordsExactas(true); return }
      }
      const rb = await geocodificar(barrioNombre)
      if (rb) {
        setCoords({ lat: rb.lat, lng: rb.lng })
        setGeoErr('No encontré la dirección exacta. Movelo en el mapa.')
      }
    } finally { setGeocodando(false) }
  }

  async function guardarSuscripcion() {
    setErr(null); setSaving(true)
    try {
      if (!form.barrioId || !form.direccion.trim()) throw new Error('Falta barrio y dirección')
      await actualizarSuscripcion(s.id, {
        alias: form.alias?.trim() || null,
        barrio_id: form.barrioId,
        barrio: barrioSeleccionado?.nombre || s.barrio,
        direccion: form.direccion.trim(),
        apto: form.apto?.trim() || null,
        tipo_vivienda: form.tipoVivienda || null,
        kit_actual: form.kitCodigo || null,
        dia_entrega: form.diaEntrega || null,
        ventana_horaria: form.ventanaHoraria,
        semana_mes: form.semanaMes || null,
        dia_cobro_mes: form.diaCobroMes ? Number(form.diaCobroMes) : null,
        modelo_cobro: form.modeloCobro,
        portero_referido_id: form.porteroReferidoId || null,
        notas: form.notas?.trim() || null,
      }, { coordsManual: coords })
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: '0 0 2px' }}>Editar suscripción</h2>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {(s.beneficiario || s.titular)?.nombre} · {s.barrio_obj?.nombre || s.barrio}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '4px 10px' }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          {[
            { id: 'suscripcion', label: '📦 Suscripción' },
            { id: 'titular', label: '💳 Titular' },
            { id: 'beneficiario', label: '🎁 Beneficiario', disabled: !s.beneficiario },
            { id: 'cobros', label: '💰 Cobros' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => !t.disabled && setTab(t.id)}
              disabled={t.disabled}
              style={{
                padding: '10px 16px', border: 0, background: 'transparent',
                fontSize: 13, fontWeight: 700, cursor: t.disabled ? 'not-allowed' : 'pointer',
                color: t.disabled ? '#ccc' : tab === t.id ? 'var(--navy)' : 'var(--muted)',
                borderBottom: tab === t.id ? '3px solid var(--navy)' : '3px solid transparent',
                marginBottom: -1,
              }}
            >{t.label}</button>
          ))}
        </div>

        {tab === 'suscripcion' && (
          <>
            <Field label="Alias">
              <input type="text" value={form.alias} onChange={e => setForm({...form, alias: e.target.value})} placeholder="Casa, Mamá, Oficina" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Barrio" required>
                <select required value={form.barrioId} onChange={e => setForm({...form, barrioId: e.target.value, diaEntrega: ''})}>
                  <option value="">— elegir —</option>
                  {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Field>
              <Field label="Tipo">
                <select value={form.tipoVivienda} onChange={e => setForm({...form, tipoVivienda: e.target.value})}>
                  <option value="">— elegir —</option>
                  <option value="casa">🏠 Casa</option>
                  <option value="edificio">🏢 Edificio</option>
                  <option value="oficina">🏢 Oficina</option>
                </select>
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <Field label="Calle + número" required>
                <input type="text" required value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} placeholder="Cavia 2858" />
              </Field>
              <Field label="Apto / Piso">
                <input type="text" value={form.apto} onChange={e => setForm({...form, apto: e.target.value})} placeholder="Apto 406" />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: '#0F1F3D', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}>
                {geocodando ? '⏳' : coords ? '🗺️ Recargar mapa' : '🗺️ Abrir mapa'}
              </button>
              {coords && coordsExactas && <span style={{ fontSize: 11, color: '#065F46', fontWeight: 700 }}>✓ pin OK</span>}
              {coords && !coordsExactas && <span style={{ fontSize: 11, color: '#92400E', fontWeight: 700 }}>⚠️ verificá el pin</span>}
              {geoErr && <span style={{ fontSize: 11, color: '#92400E' }}>{geoErr}</span>}
            </div>
            {coords && (
              <>
                <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }} height={220} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 8, padding: '10px 14px', borderRadius: 8, border: coordsExactas ? '2px solid #10B981' : '2px solid #F59E0B', background: coordsExactas ? '#ECFDF5' : '#FEF3C7', fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} style={{ width: 18, height: 18 }} />
                  <span>{coordsExactas ? '✅ Pin en la ubicación correcta' : '⚠️ Confirmá el pin'}</span>
                </label>
              </>
            )}

            <h3 className="section-head" style={{ marginTop: 16 }}>Operación</h3>
            <Field label="Kit">
              <select value={form.kitCodigo} onChange={e => setForm({...form, kitCodigo: e.target.value})}>
                <option value="">— sin kit —</option>
                {kits.map(k => <option key={k.codigo} value={k.codigo}>{k.nombre}</option>)}
              </select>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label="Día">
                <select value={form.diaEntrega} onChange={e => setForm({...form, diaEntrega: e.target.value})} disabled={!form.barrioId}>
                  <option value="">— elegir —</option>
                  {diasDisponibles.map(d => <option key={d} value={d} style={{ textTransform: 'capitalize' }}>{d}</option>)}
                </select>
              </Field>
              <Field label="Semana mes">
                <select value={form.semanaMes} onChange={e => setForm({...form, semanaMes: e.target.value})}>
                  <option value="">—</option>
                  <option value="1">1ra</option>
                  <option value="2">2da</option>
                  <option value="3">3ra</option>
                  <option value="4">4ta</option>
                </select>
              </Field>
              <Field label="Ventana">
                <select value={form.ventanaHoraria} onChange={e => setForm({...form, ventanaHoraria: e.target.value})}>
                  <option value="manana">Mañana 10-14</option>
                  <option value="tarde">Tarde 14-18</option>
                </select>
              </Field>
            </div>
            <Field label="Portero referido">
              <select value={form.porteroReferidoId} onChange={e => setForm({...form, porteroReferidoId: e.target.value})}>
                <option value="">— directo —</option>
                {porteros.map(p => <option key={p.id} value={p.id}>{p.lugar} · {p.profile?.nombre}</option>)}
              </select>
            </Field>

            <h3 className="section-head" style={{ marginTop: 16 }}>💰 Cobro</h3>
            <Field label="Modelo de cobro" required>
              <select value={form.modeloCobro} onChange={e => setForm({...form, modeloCobro: e.target.value})}>
                <option value="prepaid_mensiversario">🔄 Mensiversario (paga el mismo día cada mes)</option>
                <option value="postpaid_entrega">🤝 Al entregar (amigos/familia)</option>
                <option value="gratis">🎁 Gratis (no se cobra)</option>
              </select>
            </Field>
            {form.modeloCobro === 'prepaid_mensiversario' && (
              <Field label="Día del mes en que se cobra (1-31)" required>
                <input type="number" min="1" max="31" value={form.diaCobroMes} onChange={e => setForm({...form, diaCobroMes: e.target.value})} placeholder="15" />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  Si elegís 31 y el mes tiene 30 días, cobra el 30.
                </div>
              </Field>
            )}

            <Field label="Notas">
              <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" />
            </Field>

            {err && <div className="err-box">{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <button className="btn ghost" onClick={onClose}>Cancelar</button>
              <button className="btn primary" onClick={guardarSuscripcion} disabled={saving}>
                {saving ? 'Guardando…' : '✓ Guardar cambios'}
              </button>
            </div>
          </>
        )}

        {tab === 'titular' && s.titular && (
          <FormEditarPersona persona={s.titular} onSaved={onSaved} onClose={onClose} esTitular={true} />
        )}

        {tab === 'beneficiario' && s.beneficiario && (
          <FormEditarPersona persona={s.beneficiario} onSaved={onSaved} onClose={onClose} esTitular={false} />
        )}

        {tab === 'cobros' && (
          <TabCobros suscripcion={s} onClose={onClose} />
        )}
      </div>
    </div>
  )
}

/* ───────── Tab Cobros del cliente ───────── */
function TabCobros({ suscripcion: s, onClose }) {
  const [pagos, setPagos] = useState([])
  const [cuenta, setCuenta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRegistrar, setShowRegistrar] = useState(null)  // pago a marcar como pagado
  const [showCrearManual, setShowCrearManual] = useState(false)

  useEffect(() => { load() }, [s.id])

  async function load() {
    setLoading(true)
    const [p, c] = await Promise.all([getPagosCliente(s.id), getCuentaCobro()])
    setPagos(p); setCuenta(c)
    setLoading(false)
  }

  // Próximo pago pendiente y stats
  const pendientes = pagos.filter(p => p.estado === 'pendiente' || p.estado === 'atrasado')
  const proximo = pendientes[0]
  const totalAlDia = pagos.filter(p => p.estado === 'pagado').length
  const totalPendiente = pendientes.length

  // Texto para WhatsApp del próximo pago
  function abrirWA(pago) {
    const tel = (s.titular?.phone || '').replace(/[^\d]/g, '')
    const telWa = tel ? (tel.startsWith('598') ? tel : '598' + tel.replace(/^0+/, '')) : null
    if (!telWa) { alert('El titular no tiene teléfono cargado'); return }
    const texto = textoWhatsAppCobro({ cliente: s, pago, cuenta })
    const url = `https://wa.me/${telWa}?text=${encodeURIComponent(texto)}`
    window.open(url, '_blank')
  }

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>

  return (
    <>
      {/* Resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div style={{ padding: 14, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#065F46', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>Pagados</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#065F46' }}>{totalAlDia}</div>
        </div>
        <div style={{ padding: 14, background: totalPendiente > 0 ? '#FEF3C7' : '#F1F5F9', border: `1px solid ${totalPendiente > 0 ? '#F59E0B' : '#cbd5e1'}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#92400E', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>Pendientes</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: totalPendiente > 0 ? '#92400E' : '#475569' }}>{totalPendiente}</div>
        </div>
      </div>

      {/* Próximo cobro destacado */}
      {proximo && (
        <div style={{ background: 'linear-gradient(135deg, #00A8B5 0%, #0F1F3D 100%)', color: '#fff', padding: 16, borderRadius: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.15em', opacity: .85, marginBottom: 4 }}>PRÓXIMO COBRO</div>
          <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
            ${Number(proximo.monto).toLocaleString('es-UY')}
          </div>
          <div style={{ fontSize: 13, opacity: .9 }}>
            Vence: {proximo.vence ? new Date(proximo.vence + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' }) : '—'}
            {proximo.entrega?.numero_orden && <> · Orden {proximo.entrega.numero_orden}</>}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <button onClick={() => abrirWA(proximo)} style={{ background: '#25D366', color: '#fff', border: 0, padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              💬 Mandar WA al cliente
            </button>
            <button onClick={() => setShowRegistrar(proximo)} style={{ background: '#fff', color: '#0F1F3D', border: 0, padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              ✓ Marcar pagado
            </button>
          </div>
        </div>
      )}

      {/* Aviso si no hay cuenta configurada */}
      {!cuenta?.banco && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: '#92400E' }}>
          ⚠️ Configurá tu cuenta bancaria en <strong>Admin → Configuración</strong> para que aparezca en el WA al cliente.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 className="section-head" style={{ margin: 0 }}>Historial de pagos</h3>
        <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setShowCrearManual(true)}>+ Cobro manual</button>
      </div>

      {pagos.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          No hay pagos todavía. Se generan automáticamente al confirmar entregas.
        </div>
      ) : (
        <table className="t-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Vence</th>
              <th>Monto</th>
              <th>Método</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pagos.map(p => {
              const cfg = ESTADO_PAGO_COLOR[p.estado] || ESTADO_PAGO_COLOR.pendiente
              return (
                <tr key={p.id}>
                  <td>
                    {p.vence ? new Date(p.vence + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                    {p.entrega?.numero_orden && <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.entrega.numero_orden}</div>}
                  </td>
                  <td style={{ fontWeight: 700 }}>${Number(p.monto).toLocaleString('es-UY')}</td>
                  <td style={{ textTransform: 'capitalize', fontSize: 11, color: 'var(--muted)' }}>{p.metodo || '—'}</td>
                  <td>
                    <span style={{ background: cfg.bg, color: cfg.text, padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      ● {ESTADO_PAGO_LABEL[p.estado]}
                    </span>
                    {p.pagado_at && <div style={{ fontSize: 10, color: '#065F46', marginTop: 2 }}>{new Date(p.pagado_at).toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })}</div>}
                  </td>
                  <td>
                    {(p.estado === 'pendiente' || p.estado === 'atrasado') && (
                      <button onClick={() => setShowRegistrar(p)} style={{ padding: '3px 8px', fontSize: 10, background: '#10B981', color: '#fff', border: 0, borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}>✓ Pagado</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showRegistrar && (
        <ModalRegistrarPago pago={showRegistrar} onClose={() => setShowRegistrar(null)} onDone={() => { setShowRegistrar(null); load() }} />
      )}

      {showCrearManual && (
        <ModalCrearPagoManual clienteId={s.id} kit={s.kit} onClose={() => setShowCrearManual(false)} onDone={() => { setShowCrearManual(false); load() }} />
      )}
    </>
  )
}

function ModalRegistrarPago({ pago, onClose, onDone }) {
  const [metodo, setMetodo] = useState(pago.metodo || 'transferencia')
  const [referencia, setReferencia] = useState('')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      await registrarPago(pago.id, { metodo, referencia, notas })
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ ...modalBg, zIndex: 1100 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>Marcar como pagado</h3>
        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 0 }}>
          Monto: <strong>${Number(pago.monto).toLocaleString('es-UY')}</strong>
        </p>
        <Field label="Método">
          <select value={metodo} onChange={e => setMetodo(e.target.value)}>
            <option value="transferencia">Transferencia bancaria</option>
            <option value="efectivo">Efectivo</option>
            <option value="mercadopago">Mercado Pago link</option>
            <option value="handy">Handy</option>
            <option value="otro">Otro</option>
          </select>
        </Field>
        <Field label="Nro de referencia / comprobante">
          <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} placeholder="Nº de transferencia" />
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : '✓ Confirmar pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalCrearPagoManual({ clienteId, kit, onClose, onDone }) {
  const [monto, setMonto] = useState(kit?.precio_susc || '')
  const [vence, setVence] = useState(new Date().toISOString().slice(0, 10))
  const [metodo, setMetodo] = useState('transferencia')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      if (!monto || Number(monto) <= 0) throw new Error('Monto inválido')
      await crearPagoManual({
        clienteId,
        monto,
        mes: vence.slice(0, 7) + '-01',  // primer día del mes del vencimiento
        vence,
        metodo,
        notas,
      })
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ ...modalBg, zIndex: 1100 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 420 }}>
        <h3 style={{ marginTop: 0 }}>+ Cobro manual</h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0 }}>
          Para retroactivos, extras o casos especiales fuera del flujo automático.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Monto" required>
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="1500" />
          </Field>
          <Field label="Vence" required>
            <input type="date" value={vence} onChange={e => setVence(e.target.value)} />
          </Field>
        </div>
        <Field label="Método">
          <select value={metodo} onChange={e => setMetodo(e.target.value)}>
            <option value="transferencia">Transferencia</option>
            <option value="efectivo">Efectivo</option>
            <option value="mercadopago">Mercado Pago</option>
            <option value="handy">Handy</option>
            <option value="otro">Otro</option>
          </select>
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" placeholder="Por ejemplo: kit de prueba, reposición, etc." />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : '+ Crear cobro'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* Form para editar datos de una persona (titular o beneficiario) */
function FormEditarPersona({ persona, onSaved, onClose, esTitular }) {
  const [f, setF] = useState({
    nombre: persona.nombre || '',
    phone: persona.phone || '',
    email: persona.email || '',
    documento: persona.documento || '',
    documento_tipo: persona.documento_tipo || 'ci',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    setErr(null); setSaving(true)
    try {
      if (!f.nombre.trim()) throw new Error('Falta el nombre')
      await actualizarPersona(persona.id, f)
      onSaved()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <>
      <Field label="Nombre" required>
        <input type="text" value={f.nombre} onChange={e => setF({...f, nombre: e.target.value})} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8 }}>
        <Field label="Tipo doc">
          <select value={f.documento_tipo} onChange={e => setF({...f, documento_tipo: e.target.value})}>
            <option value="ci">CI</option>
            <option value="rut">RUT</option>
            <option value="pasaporte">Pasaporte</option>
          </select>
        </Field>
        <Field label="Documento">
          <input type="text" value={f.documento} onChange={e => setF({...f, documento: e.target.value})} />
        </Field>
      </div>
      <Field label="Email">
        <input type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} />
      </Field>
      <Field label="Teléfono">
        <input type="tel" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} />
      </Field>
      {err && <div className="err-box">{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={guardar} disabled={saving || !f.nombre.trim()}>
          {saving ? 'Guardando…' : `✓ Guardar ${esTitular ? 'titular' : 'beneficiario'}`}
        </button>
      </div>
    </>
  )
}

const modalBg = {
  position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, overflowY: 'auto',
}
const modalCard = {
  background: '#fff', borderRadius: 14, padding: 24,
  maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto',
}
